import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IntelligenceFlowProps {
  className?: string;
  animated?: boolean;
}

const INPUTS = [
  { label: 'diagram.flow.input.documents', icon: 'M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z' },
  { label: 'diagram.flow.input.erp', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
  { label: 'diagram.flow.input.crm', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { label: 'diagram.flow.input.databases', icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' },
  { label: 'diagram.flow.input.apis', icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { label: 'diagram.flow.input.bi', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6h2zm8-12V5a2 2 0 00-2-2H5a2 2 0 00-2 2v2h2zm0 4V9a2 2 0 00-2-2H5a2 2 0 00-2 2v2h2zm0 4v-2a2 2 0 00-2-2H5a2 2 0 00-2 2v2h2z' },
];

const OUTPUTS = [
  { label: 'diagram.flow.output.assistant', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
  { label: 'diagram.flow.output.dashboards', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
  { label: 'diagram.flow.output.reports', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { label: 'diagram.flow.output.automation', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { label: 'diagram.flow.output.applications', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { label: 'diagram.flow.output.decisions', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
];

function buildPath(startX: number, startY: number, endX: number, endY: number) {
  const midX = (startX + endX) / 2;
  return `M${startX},${startY} C${midX},${startY} ${midX},${endY} ${endX},${endY}`;
}

export default function IntelligenceFlow({ className = '', animated = true }: IntelligenceFlowProps) {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!animated) return;
    const timer = setTimeout(() => setIsActive(true), 500);
    return () => clearTimeout(timer);
  }, [animated]);

  const inputPositions = INPUTS.map((_, i) => ({
    x: 60,
    y: 55 + i * 58,
    label: _.label,
    icon: _.icon,
  }));

  const outputPositions = OUTPUTS.map((_, i) => ({
    x: 540,
    y: 55 + i * 58,
    label: _.label,
    icon: _.icon,
  }));

  const coreX = 300;
  const coreY = 200;

  return (
    <div className={`relative ${className}`}>
      <svg
        ref={svgRef}
        viewBox="0 0 600 420"
        className="w-full h-auto"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Glow filter for core */}
          <filter id="coreGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Gradient for core */}
          <linearGradient id="coreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7057FF" />
            <stop offset="50%" stopColor="#22D3C5" />
            <stop offset="100%" stopColor="#7057FF" />
          </linearGradient>

          {/* Gradient for data lines */}
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22D3C5" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#7057FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#22D3C5" stopOpacity="0.3" />
          </linearGradient>

          {/* Particle glow */}
          <filter id="particleGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background panel */}
        <rect x="20" y="10" width="560" height="400" rx="16" fill="oklch(var(--background-100))" opacity="0.5" />

        {/* Input zone label */}
        <text x="60" y="30" textAnchor="middle" fill="oklch(var(--foreground-500))" fontSize="10" fontFamily="var(--font-mono)" fontWeight="500" letterSpacing="2">
          {t('diagram.flow.zone.systems')}
        </text>

        {/* Output zone label */}
        <text x="540" y="30" textAnchor="middle" fill="oklch(var(--foreground-500))" fontSize="10" fontFamily="var(--font-mono)" fontWeight="500" letterSpacing="2">
          {t('diagram.flow.zone.outputs')}
        </text>

        {/* Central zone label */}
        <text x="300" y="30" textAnchor="middle" fill="oklch(var(--accent-500))" fontSize="10" fontFamily="var(--font-mono)" fontWeight="500" letterSpacing="2">
          {t('diagram.flow.zone.core')}
        </text>

        {/* Connection lines from inputs to core */}
        {inputPositions.map((input, i) => {
          const path = buildPath(input.x + 64, input.y, coreX - 45, coreY);
          return (
            <g key={`in-line-${i}`}>
              <path
                d={path}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="1"
                opacity={isActive ? 0.5 : 0.2}
                className={animated ? 'transition-opacity duration-1000' : ''}
                style={{ transitionDelay: `${i * 150}ms` }}
              />
              {/* Animated data packet */}
              {animated && isActive && (
                <circle r="3" fill="#22D3C5" filter="url(#particleGlow)">
                  <animateMotion
                    dur={`${2.5 + i * 0.3}s`}
                    repeatCount="indefinite"
                    path={path}
                  />
                </circle>
              )}
              {animated && isActive && (
                <circle r="2" fill="#7057FF" opacity="0.7">
                  <animateMotion
                    dur={`${3 + i * 0.4}s`}
                    repeatCount="indefinite"
                    path={path}
                    begin={`${0.5 + i * 0.2}s`}
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* Connection lines from core to outputs */}
        {outputPositions.map((output, i) => {
          const path = buildPath(coreX + 45, coreY, output.x - 64, output.y);
          return (
            <g key={`out-line-${i}`}>
              <path
                d={path}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="1"
                opacity={isActive ? 0.5 : 0.2}
                className={animated ? 'transition-opacity duration-1000' : ''}
                style={{ transitionDelay: `${300 + i * 150}ms` }}
              />
              {/* Animated data packet */}
              {animated && isActive && (
                <circle r="3" fill="#7057FF" filter="url(#particleGlow)">
                  <animateMotion
                    dur={`${2.5 + i * 0.3}s`}
                    repeatCount="indefinite"
                    path={path}
                    begin={`${0.3 + i * 0.15}s`}
                  />
                </circle>
              )}
              {animated && isActive && (
                <circle r="2" fill="#22D3C5" opacity="0.7">
                  <animateMotion
                    dur={`${3 + i * 0.4}s`}
                    repeatCount="indefinite"
                    path={path}
                    begin={`${0.8 + i * 0.25}s`}
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* Input nodes */}
        {inputPositions.map((input, i) => (
          <g
            key={`input-${i}`}
            className={animated ? 'transition-all duration-700' : ''}
            style={{ transitionDelay: `${i * 100}ms`, opacity: isActive ? 1 : 0.6, transform: isActive ? 'translate(0,0)' : 'translate(-10px,0)' }}
          >
            {/* Node background */}
            <rect
              x={input.x - 64}
              y={input.y - 21}
              width={128}
              height={42}
              rx="9"
              fill="oklch(var(--background-50))"
              stroke="oklch(var(--secondary-300))"
              strokeWidth="1"
              opacity="0.9"
            />
            {/* Icon */}
            <g transform={`translate(${input.x - 43}, ${input.y - 10})`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22D3C5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={input.icon} />
              </svg>
            </g>
            {/* Label */}
            <text
              x={input.x - 18}
              y={input.y + 4}
              fill="oklch(var(--foreground-700))"
              fontSize="10"
              fontFamily="var(--font-label)"
              fontWeight="500"
            >
              {t(input.label)}
            </text>
          </g>
        ))}

        {/* Output nodes */}
        {outputPositions.map((output, i) => (
          <g
            key={`output-${i}`}
            className={animated ? 'transition-all duration-700' : ''}
            style={{ transitionDelay: `${400 + i * 100}ms`, opacity: isActive ? 1 : 0.6, transform: isActive ? 'translate(0,0)' : 'translate(10px,0)' }}
          >
            {/* Node background */}
            <rect
              x={output.x - 64}
              y={output.y - 21}
              width={128}
              height={42}
              rx="9"
              fill="oklch(var(--background-50))"
              stroke="oklch(var(--accent-300))"
              strokeWidth="1"
              opacity="0.9"
            />
            {/* Icon */}
            <g transform={`translate(${output.x - 43}, ${output.y - 10})`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7057FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={output.icon} />
              </svg>
            </g>
            {/* Label */}
            <text
              x={output.x - 18}
              y={output.y + 4}
              fill="oklch(var(--foreground-700))"
              fontSize="10"
              fontFamily="var(--font-label)"
              fontWeight="500"
            >
              {t(output.label)}
            </text>
          </g>
        ))}

        {/* Central Core */}
        <g className={isActive && animated ? 'animate-[corePulse_3s_ease-in-out_infinite]' : ''}>
          {/* Outer glow ring */}
          <circle
            cx={coreX}
            cy={coreY}
            r={84}
            fill="none"
            stroke="url(#coreGradient)"
            strokeWidth="0.5"
            opacity={0.3}
            className={animated && isActive ? 'animate-[spin_20s_linear_infinite]' : ''}
          />
          <circle
            cx={coreX}
            cy={coreY}
            r={76}
            fill="none"
            stroke="url(#coreGradient)"
            strokeWidth="0.5"
            opacity={0.2}
            strokeDasharray="4 8"
            className={animated && isActive ? 'animate-[spin_15s_linear_infinite_reverse]' : ''}
          />

          {/* Core hexagon */}
          <g filter="url(#coreGlow)">
            <polygon
              points={`${coreX},${coreY - 44} ${coreX + 72},${coreY - 22} ${coreX + 72},${coreY + 22} ${coreX},${coreY + 44} ${coreX - 72},${coreY + 22} ${coreX - 72},${coreY - 22}`}
              fill="oklch(var(--primary-500))"
              stroke="url(#coreGradient)"
              strokeWidth="2"
            />
            {/* Inner hexagon */}
            <polygon
              points={`${coreX},${coreY - 34} ${coreX + 58},${coreY - 17} ${coreX + 58},${coreY + 17} ${coreX},${coreY + 34} ${coreX - 58},${coreY + 17} ${coreX - 58},${coreY - 17}`}
              fill="none"
              stroke="#22D3C5"
              strokeWidth="1"
              opacity="0.6"
            />
          </g>

          {/* MorphenIQ logo in center */}
          <image
            href="https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/ed6231ec-ad64-40b6-9b06-9ed759a36c3f_compressed_ese.webp"
            x={coreX - 72}
            y={coreY - 38}
            width={144}
            height={58}
            preserveAspectRatio="xMidYMid meet"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </g>

        {/* No bottom caption inside SVG — moved to HTML below */}
      </svg>
    </div>
  );
}